import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Plus, X, Trash2, Upload, Pencil, Phone, MapPin } from "lucide-react";
import { CountrySelect } from "@/components/CountrySelect";
import { useTwilioVoice } from "@/context/TwilioVoiceContext";
import RepLocationModal from "@/components/RepLocationModal";

export default function SalesReps() {
  const { t, lang } = useI18n();
  const tv = useTwilioVoice();
  const L = lang === "el";
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null); // user object when editing
  const blankForm = { name: "", email: "", password: "", country: "", country_code: "", phone: "", photo_url: "", languages_spoken: "", industries: "", experience_years: 0, assigned_countries: "", forward_when_offline: true };
  const [form, setForm] = useState(blankForm);
  const [err, setErr] = useState("");
  const [locateRep, setLocateRep] = useState(null); // rep being geo-located

  const load = async () => {
    const { data } = await api.get("/users");
    setList(data.filter((u) => u.role === "sales_rep"));
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(blankForm); setErr(""); setOpen(true); };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      name: u.name || "",
      email: u.email || "",
      password: "",  // blank = leave unchanged
      country: u.country || "",
      country_code: u.country_code || "",
      phone: u.phone || "",
      photo_url: u.photo_url || "",
      languages_spoken: (u.languages_spoken || []).join(", "),
      industries: (u.industries || []).join(", "),
      experience_years: u.experience_years || 0,
      assigned_countries: (u.assigned_countries || []).join(", "),
      forward_when_offline: u.forward_when_offline !== false,
    });
    setErr("");
    setOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const payload = {
        ...form,
        experience_years: parseInt(form.experience_years) || 0,
        languages_spoken: form.languages_spoken.split(",").map((s) => s.trim()).filter(Boolean),
        industries: form.industries.split(",").map((s) => s.trim()).filter(Boolean),
        assigned_countries: form.assigned_countries.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (editing) {
        if (!payload.password) delete payload.password;
        await api.patch(`/users/${editing.id}`, payload);
      } else {
        await api.post("/users/sales-rep", payload);
      }
      setOpen(false);
      setForm(blankForm);
      load();
    } catch (e) {
      setErr(e.response?.data?.detail || "Error");
    }
  };
  const del = async (id) => {
    if (!window.confirm(L ? "Διαγραφή πωλητή;" : "Remove rep?")) return;
    await api.delete(`/users/${id}`); load();
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const { data } = await api.post("/files/upload-public", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setForm((f) => ({ ...f, photo_url: data.url }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500">{t("nav.sales_reps")}</div>
          <h1 className="font-heading text-3xl font-bold text-slate-900 tracking-tight mt-1">{L ? "Πωλητές" : "Sales Representatives"}</h1>
        </div>
        <button data-testid="rep-create-button" onClick={openCreate} className="inline-flex items-center gap-2 px-4 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold">
          <Plus className="w-4 h-4" /> {t("common.add")}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="reps-grid">
        {list.length === 0 ? (
          <div className="col-span-full text-center text-slate-400 py-16 bg-white border border-slate-200 rounded-lg">{t("common.no_data")}</div>
        ) : list.map((r) => (
          <div key={r.id} className="bg-white border border-slate-200 rounded-lg p-5 flex gap-4 hover:-translate-y-0.5 hover:shadow-sm transition-all">
            <div className="w-16 h-16 rounded-full bg-slate-100 overflow-hidden flex-shrink-0">
              {r.photo_url ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}${r.photo_url}`} alt={r.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400 font-semibold text-lg">{r.name?.[0]}</div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <h3 className="font-heading font-semibold text-slate-900 truncate">{r.name}</h3>
                <div className="flex items-center gap-1">
                  {tv.enabled && (
                    <button
                      data-testid={`rep-call-${r.id}`}
                      onClick={() => tv.startCall(`user:${r.id}`)}
                      disabled={!tv.deviceReady || tv.outOfMinutes || tv.status !== "idle"}
                      title={L ? "Κλήση πωλητή" : "Call rep"}
                      className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded disabled:opacity-30 disabled:text-slate-400"
                    >
                      <Phone className="w-4 h-4" />
                    </button>
                  )}
                  <button
                    data-testid={`rep-locate-${r.id}`}
                    onClick={() => setLocateRep(r)}
                    title={L ? "Πού βρίσκεται τώρα;" : "Where is this rep?"}
                    className="p-1.5 text-[#0052CC] hover:bg-blue-50 rounded"
                  >
                    <MapPin className="w-4 h-4" />
                  </button>
                  <button data-testid={`rep-edit-${r.id}`} onClick={() => openEdit(r)} className="p-1.5 text-slate-400 hover:text-[#0052CC]" title={L ? "Επεξεργασία" : "Edit"}><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`rep-delete-${r.id}`} onClick={() => del(r.id)} className="p-1.5 text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="text-sm text-slate-600 truncate">{r.email}</div>
              <div className="mt-2 text-xs text-slate-500">
                {r.country && <span>{r.country}</span>}
                {r.experience_years ? <span> · {r.experience_years}y exp</span> : null}
              </div>
              {r.languages_spoken?.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {r.languages_spoken.map((l) => <span key={l} className="text-[10px] uppercase tracking-wider px-2 py-0.5 bg-slate-100 rounded text-slate-700 font-semibold">{l}</span>)}
                </div>
              )}
              {r.industries?.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {r.industries.slice(0, 3).map((i) => <span key={i} className="text-[10px] px-2 py-0.5 bg-[#0052CC]/10 text-[#0052CC] rounded">{i}</span>)}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-lg max-w-lg w-full border border-slate-200 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-heading font-semibold text-slate-900">
                {editing ? (L ? "Επεξεργασία πωλητή" : "Edit sales rep") : (L ? "Νέος πωλητής" : "Add sales representative")}
              </h3>
              <button onClick={() => setOpen(false)}><X className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="px-6 py-4 space-y-3" data-testid="rep-form">
              <Inp label={L ? "Όνομα" : "Name"} testid="rep-name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
              <Inp label="Email" testid="rep-email" type="email" required value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">
                  {editing ? (L ? "Νέος κωδικός (αφήστε κενό για διατήρηση)" : "New password (leave empty to keep)") : (L ? "Κωδικός" : "Password")}
                </label>
                <input data-testid="rep-password" type="password" required={!editing} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" placeholder={editing ? "••••••••" : ""} />
              </div>
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{L ? "Χώρα" : "Country"}</label>
                <CountrySelect testid="rep-country-select" value={form.country_code} onChange={(code) => setForm({ ...form, country_code: code, country: code })} />
              </div>
              <Inp label={L ? "Έτη εμπειρίας" : "Years experience"} testid="rep-exp" type="number" value={form.experience_years} onChange={(v) => setForm({ ...form, experience_years: v })} />
              <Inp label={L ? "Γλώσσες (κόμμα)" : "Languages (comma)"} testid="rep-langs" value={form.languages_spoken} onChange={(v) => setForm({ ...form, languages_spoken: v })} />
              <Inp label={L ? "Κλάδοι (κόμμα)" : "Industries (comma)"} testid="rep-industries" value={form.industries} onChange={(v) => setForm({ ...form, industries: v })} />
              <Inp label={L ? "Ανατεθειμένες χώρες (κόμμα)" : "Assigned countries (comma)"} testid="rep-assigned" value={form.assigned_countries} onChange={(v) => setForm({ ...form, assigned_countries: v })} />
              <div>
                <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{L ? "Φωτογραφία" : "Photo"}</label>
                <div className="mt-1.5 flex items-center gap-3">
                  {form.photo_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${form.photo_url}`} alt="" className="w-12 h-12 rounded-full object-cover" />}
                  <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-2 border border-dashed border-slate-300 rounded-md text-sm text-slate-600">
                    <Upload className="w-4 h-4" /> Upload
                    <input data-testid="rep-photo-upload" type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
                  </label>
                </div>
              </div>
              {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-md">{err}</div>}
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 border border-slate-200 rounded-md text-sm font-medium">{t("common.cancel")}</button>
                <button type="submit" data-testid="rep-submit" className="px-4 py-2 bg-[#0052CC] text-white rounded-md text-sm font-semibold">
                  {editing ? (L ? "Αποθήκευση" : "Save") : t("common.create")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {locateRep && (
        <RepLocationModal
          repId={locateRep.id}
          repName={locateRep.name}
          onClose={() => setLocateRep(null)}
          L={L}
        />
      )}
    </div>
  );
}

function Inp({ label, testid, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-700">{label}</label>
      <input data-testid={testid} type={type} required={required} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1.5 w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
    </div>
  );
}
